import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { CommonService } from 'src/app/shared/common.service';
import * as settingsActions from './settings.actions';
import { catchError, finalize, map, of, switchMap, tap } from 'rxjs';
import { APIResponse } from 'src/app/core/models/api-response';
import { AlertEnums } from 'src/app/shared/enums/shared.enums';
import { SettingsService } from '../services/settings.services';
import { ICustomField, ILabel, ILabelOption, IModule, INotification, INotificationTypeUpdate, IObjectCustomFields, IOrdersSettings, IRole, ISplittingRule, ITeam, ITeamOption, IUser } from '../models/settings.models';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';

@Injectable()
export class SettingsEffects {
  constructor(
    private actions$: Actions,
    public store: Store<AppState>,
    private settingsService: SettingsService,
    private commonService: CommonService,
    private translateService: TranslateService
  ) {}

  // Get Me
  GetMeEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.GetMe),
      tap(() => {
        this.store.dispatch(settingsActions.SetUsersLoader({ isLoading: true }));
      }),
      switchMap(() => this.settingsService.getMe().pipe(
        map((res: APIResponse<IUser>) => settingsActions.GetMeSuccess({ user: res.object! })),
        catchError(() => of(settingsActions.GetMeError())),
        finalize(() => {
          this.store.dispatch(settingsActions.SetUsersLoader({ isLoading: false }));
        })
      ))
    )
  );

  // Update Me
  UpdateMeEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateMe),
      tap(() => {
        this.store.dispatch(settingsActions.SetUsersLoader({ isLoading: true }));
      }),
      switchMap((action) => this.settingsService.updateMe(action.user).pipe(
        map((res: APIResponse<IUser>) => settingsActions.UpdateMeSuccess({ user: res.object! })),
        tap(() => {
          this.commonService.showToast(
            this.translateService.instant(`alerts.${AlertEnums.userUpdateSuccess}`),
            'SUCCESS'
          );
        }),
        catchError(() => of(settingsActions.UpdateMeError())),
        finalize(() => {
          this.store.dispatch(settingsActions.SetUsersLoader({ isLoading: false }));
        })
      ))
    )
  );

  // fetch All Users
  FetchAllUsersEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchAllUsers),
      switchMap((action) =>
        this.settingsService.listAllUsers(action.enabled, action.deleted).pipe(
          map((res: APIResponse<IUser>) =>
            settingsActions.FetchAllUsersSuccess({ users: res.data })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.SetUsersLoader({ isLoading: false })
            );
          }),
          catchError(() =>
            of(settingsActions.FetchAllUsersError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetUsersLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetUsersLoader({ isLoading: false })
        );
      })
    )
  );

  // Set language
  setLanguage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.setLanguage),
      switchMap(({ lang }) =>
        this.settingsService.setLanguage(lang).pipe(
          map(() => settingsActions.setLanguageSuccess({ lang })),
          catchError((error) => of(settingsActions.setLanguageError({ error })))
        )
      )
    )
  );

  // fetch Users
  FetchUsersEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchUsers),
      switchMap((action) =>
        this.settingsService.listUsers(action.page, action.enabled, action.deleted, action.search).pipe(
          map((res: APIResponse<IUser>) =>
            settingsActions.FetchUsersSuccess({ users: res })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.SetUsersLoader({ isLoading: false })
            );
          }),
          catchError(() =>
            of(settingsActions.FetchUsersError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetUsersLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetUsersLoader({ isLoading: false })
        );
      })
    )
  );

  // add User
  AddUserEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.AddUser),
      switchMap((action) =>
        this.settingsService.addUser(action.user).pipe(
          map((res: APIResponse<IUser>) =>
            settingsActions.AddUserSuccess({ user: res.data[0] })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.FetchUsers({ page: { number: 1, size: DEFAULT_PAGE_SIZE } })
            );
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.userAddSuccess}`), 'SUCCESS');
          }),
          catchError(() =>of(settingsActions.AddUserError()))
        )
      )
    )
  );

  // Update User
  UpdateUserEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateUser),
      switchMap((action) =>
        this.settingsService.updateUser(action.id, action.user).pipe(
          map((res: APIResponse<IUser>) => {
            const updatedUser = res.object!;
            return settingsActions.UpdateUserSuccess({ user: updatedUser })
          }),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant(`alerts.${AlertEnums.userUpdateSuccess}`),
              'SUCCESS'
            );
          }),
          catchError(() => of(settingsActions.UpdateUserError()))
        )
      )
    )
  );

  // Delete User
  DeleteUserEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.DeleteUser),
      switchMap((action) =>
        this.settingsService.deleteUser(action.id).pipe(
          map((res: APIResponse<IUser>) => settingsActions.DeleteUserSuccess()),
          tap(() => {
            this.store.dispatch(
              settingsActions.FetchUsers({ page: { number: 1, size: DEFAULT_PAGE_SIZE } })
            );
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.userDeleteSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.DeleteUserError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetUsersLoader({ isLoading: false })
                );
              })
            )
          )
        )
      )
    )
  );

  // Restore soft-deleted user
  RestoreUserEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.RestoreUser),
      switchMap((action) =>
        this.settingsService.restoreUser(action.id).pipe(
          map((res: APIResponse<IUser>) =>
            settingsActions.RestoreUserSuccess({ user: res.data[0] })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.FetchUsers({ page: { number: 1, size: DEFAULT_PAGE_SIZE }, deleted: 'true' })
            );
            this.commonService.showToast(
              this.translateService.instant('alerts.userRestoreSuccess'),
              'SUCCESS'
            );
          }),
          catchError(() => of(settingsActions.RestoreUserError()))
        )
      )
    )
  );

  // fetch All Roles
  FetchAllRolesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchAllRoles),
      switchMap(() =>
        this.settingsService.listAllRoles().pipe(
          map((res: APIResponse<IRole>) =>
            settingsActions.FetchAllRolesSuccess({ roles: res.data })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.SetRolesLoader({ isLoading: false })
            );
          }),
          catchError(() =>
            of(settingsActions.FetchAllRolesError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetRolesLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetRolesLoader({ isLoading: false })
        );
      })
    )
  );

  // fetch Roles
  FetchRolesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchRoles),
      switchMap((action) =>
        this.settingsService.listRoles(action.page).pipe(
          map((res: APIResponse<IRole>) =>
            settingsActions.FetchRolesSuccess({ roles: res })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.SetRolesLoader({ isLoading: false })
            );
          }),
          catchError(() =>
            of(settingsActions.FetchRolesError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetRolesLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetRolesLoader({ isLoading: false })
        );
      })
    )
  );

  // add Role
  AddRoleEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.AddRole),
      switchMap((action) =>
        this.settingsService.addRole(action.role).pipe(
          map((res: APIResponse<IRole>) =>
            settingsActions.AddRoleSuccess({ role: res.data[0]! })
          ),
          tap(() => {
            this.store.dispatch(settingsActions.FetchAllRoles());
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.roleAddSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.AddRoleError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetRolesLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetRolesLoader({ isLoading: false })
        );
      })
    )
  );

  // Update Role
  UpdateRoleEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateRole),
      switchMap((action) =>
        this.settingsService.updateRole(action.id, action.role).pipe(
          map((res: APIResponse<IRole>) =>
            settingsActions.UpdateRoleSuccess({ role: res.object! })
          ),
          tap(() => {
            this.store.dispatch(settingsActions.FetchAllRoles());
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.roleUpdateSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.UpdateRoleError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetRolesLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetRolesLoader({ isLoading: false })
        );
      })
    )
  );

  // Delete Role
  DeleteRoleEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.DeleteRole),
      switchMap((action) =>
        this.settingsService.deleteRole(action.id).pipe(
          map((res: APIResponse<IRole>) => settingsActions.DeleteRoleSuccess()),
          tap(() => {
            this.store.dispatch(
              settingsActions.FetchRoles({ page: { number: 1, size: DEFAULT_PAGE_SIZE } })
            );
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.roleDeleteSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.DeleteRoleError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetRolesLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetRolesLoader({ isLoading: false })
        );
      })
    )
  );

  // fetch All Teams
  FetchAllTeamsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchAllTeams),
      switchMap((action) =>
        this.settingsService.listAllTeams(action.sort).pipe(
          map((res: APIResponse<ITeam>) =>
            settingsActions.FetchAllTeamsSuccess({ teams: res.data })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.SetTeamsLoader({ isLoading: false })
            );
          }),
          catchError(() =>
            of(settingsActions.FetchAllTeamsError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetTeamsLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetTeamsLoader({ isLoading: false })
        );
      })
    )
  );

  // fetch the team options for filter pickers
  // No `SetTeamsLoader` here: this feeds a dropdown inside the Orders / Inbox filter panels,
  // and flipping the shared settings `loading` flag from there would blank unrelated
  // settings screens. A failure leaves the previous options in place rather than clearing
  // them, so a transient 5xx does not empty a picker the user is mid-way through.
  FetchTeamOptionsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchTeamOptions),
      switchMap(() =>
        this.settingsService.listTeamOptions().pipe(
          map((res: APIResponse<ITeamOption>) =>
            settingsActions.FetchTeamOptionsSuccess({ teams: res.data ?? [] })
          ),
          catchError(() => of(settingsActions.FetchTeamOptionsError()))
        )
      )
    )
  );

  // add Team
  AddTeamEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.AddTeam),
      switchMap((action) =>
        this.settingsService.addTeam(action.team).pipe(
          // Create wraps the new team in a LIST under `data` — update wraps it as a dict
          // under `object`. Reading the wrong one hands the reducer `undefined`.
          map((res: APIResponse<ITeam>) =>
            settingsActions.AddTeamSuccess({ team: res.data[0]! })
          ),
          tap(() => {
            this.store.dispatch(settingsActions.FetchAllTeams({}));
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.teamAddSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.AddTeamError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetTeamsLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetTeamsLoader({ isLoading: false })
        );
      })
    )
  );

  // Update Team
  UpdateTeamEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateTeam),
      switchMap((action) =>
        this.settingsService.updateTeam(action.id, action.team).pipe(
          // `object`, not `data[0]` — see AddTeamEffect$ above.
          map((res: APIResponse<ITeam>) =>
            settingsActions.UpdateTeamSuccess({ team: res.object! })
          ),
          tap(() => {
            this.store.dispatch(settingsActions.FetchAllTeams({}));
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.teamUpdateSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.UpdateTeamError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetTeamsLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetTeamsLoader({ isLoading: false })
        );
      })
    )
  );

  // Delete Team
  DeleteTeamEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.DeleteTeam),
      switchMap((action) =>
        this.settingsService.deleteTeam(action.id).pipe(
          map(() => settingsActions.DeleteTeamSuccess()),
          tap(() => {
            // The Teams list is unpaginated, so it refreshes through FetchAllTeams —
            // DeleteRoleEffect$ re-dispatches a paged FetchRoles, which would leave this
            // page showing the deleted row.
            this.store.dispatch(settingsActions.FetchAllTeams({}));
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.teamDeleteSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.DeleteTeamError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetTeamsLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetTeamsLoader({ isLoading: false })
        );
      })
    )
  );

  // ── Labels ──────────────────────────────────────────────────────────────
  // Every mutation below re-dispatches `FetchAllLabels` rather than trusting its
  // own response: the list is unpaginated and the create response carries no
  // position, so a refetch is the only thing that keeps ordering honest.
  FetchLabelOptionsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchLabelOptions),
      switchMap(() =>
        this.settingsService.listLabelOptions().pipe(
          map((res: APIResponse<ILabelOption>) =>
            settingsActions.FetchLabelOptionsSuccess({ labels: res.data ?? [] })
          ),
          catchError(() => of(settingsActions.FetchLabelOptionsError()))
        )
      )
    )
  );

  FetchAllLabelsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchAllLabels),
      switchMap(() =>
        this.settingsService.listAllLabels().pipe(
          // `?? []` because an empty tenant is a 200 with an empty list, which is an
          // empty state and not an error — CRUD-03.
          map((res: APIResponse<ILabel>) =>
            settingsActions.FetchAllLabelsSuccess({ labels: res.data ?? [] })
          ),
          tap(() => {
            this.store.dispatch(settingsActions.SetLabelsLoader({ isLoading: false }));
          }),
          catchError(() =>
            of(settingsActions.FetchAllLabelsError()).pipe(
              tap(() => {
                this.store.dispatch(settingsActions.SetLabelsLoader({ isLoading: false }));
              })
            )
          )
        )
      )
    )
  );

  AddLabelEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.AddLabel),
      switchMap((action) =>
        this.settingsService.addLabel(action.label).pipe(
          // `object`, NOT `data[0]`. This is where labels diverge from teams:
          // `POST /v1/teams` wraps its single result in a list, `POST /v1/labels`
          // does not. Reading `data[0]` here hands the reducer `undefined`.
          map((res: APIResponse<ILabel>) =>
            settingsActions.AddLabelSuccess({ label: res.object! })
          ),
          tap(() => {
            this.store.dispatch(settingsActions.FetchAllLabels());
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.labelAddSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.AddLabelError()).pipe(
              tap(() => {
                this.store.dispatch(settingsActions.SetLabelsLoader({ isLoading: false }));
              })
            )
          )
        )
      )
    )
  );

  UpdateLabelEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateLabel),
      switchMap((action) =>
        this.settingsService.updateLabel(action.id, action.label).pipe(
          map((res: APIResponse<ILabel>) =>
            settingsActions.UpdateLabelSuccess({ label: res.object! })
          ),
          tap(() => {
            this.store.dispatch(settingsActions.FetchAllLabels());
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.labelUpdateSuccess}`), 'SUCCESS');
          }),
          // A `provider_sync` name or description edit is a 400 from the backend.
          // The form does not offer that edit, so reaching here means something
          // routed around it; the interceptor toasts, and the dialog is released
          // by `AddLabelError` / `UpdateLabelError` rather than left spinning.
          catchError(() =>
            of(settingsActions.UpdateLabelError()).pipe(
              tap(() => {
                this.store.dispatch(settingsActions.SetLabelsLoader({ isLoading: false }));
              })
            )
          )
        )
      )
    )
  );

  DeleteLabelEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.DeleteLabel),
      switchMap((action) =>
        this.settingsService.deleteLabel(action.id).pipe(
          // The id comes from the request, not the response: the delete route
          // answers `{id}` but the reducer must not depend on that echo.
          map(() => settingsActions.DeleteLabelSuccess({ id: action.id })),
          tap(() => {
            this.store.dispatch(settingsActions.FetchAllLabels());
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.labelDeleteSuccess}`), 'SUCCESS');
          }),
          catchError(() =>
            of(settingsActions.DeleteLabelError()).pipe(
              tap(() => {
                this.store.dispatch(settingsActions.SetLabelsLoader({ isLoading: false }));
              })
            )
          )
        )
      )
    )
  );

  // fetch All Modules
  FetchAllModulesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchAllModules),
      switchMap(() =>
        this.settingsService.listAllModules().pipe(
          map((res: APIResponse<IModule>) =>
            settingsActions.FetchAllModulesSuccess({ modules: res.data })
          ),
          tap(() => {
            this.store.dispatch(
              settingsActions.SetModulesLoader({ isLoading: false })
            );
          }),
          catchError(() =>
            of(settingsActions.FetchAllModulesError()).pipe(
              tap(() => {
                this.store.dispatch(
                  settingsActions.SetModulesLoader({ isLoading: false })
                );
              })
            )
          )
        )
      ),
      finalize(() => {
        this.store.dispatch(
          settingsActions.SetModulesLoader({ isLoading: false })
        );
      })
    )
  );

  // Get notifications
  ListNotificationTypesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.getNotifications),
      tap(() => {
        this.store.dispatch(settingsActions.SetNotificationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.listNotificationTypes().pipe(
          map((res: APIResponse<INotification>) => settingsActions.getNotificationsSuccess({ notifications: res.data })),
          catchError(() => of(
            settingsActions.getNotificationsError()
          )),
          finalize(() => {
            this.store.dispatch(settingsActions.SetNotificationsLoader({ isLoading: false }));
          })
        )
      ),
    )
  );

  // update notification type
  UpdateNotificationTypeEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.updateNotifications),
      tap(() => {
        this.store.dispatch(settingsActions.SetNotificationsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.updateNotifications(action.id, action.notificationType).pipe(
          map((res: APIResponse<INotificationTypeUpdate>) => settingsActions.updateNotificationsSuccess({ notificationType: res.object! })),
          tap(() => {
            this.store.dispatch(settingsActions.getNotifications());
            this.commonService.showToast(this.translateService.instant(`${AlertEnums.updateSuccess}`), 'SUCCESS');
          }),
          catchError(() => of(settingsActions.updateNotificationsError())),
          finalize(() => {
            this.store.dispatch(settingsActions.getNotifications());
            this.store.dispatch(settingsActions.SetNotificationsLoader({ isLoading: false }));
          })
        )
      ),
    )
  );

  // fetch All Custom Fields
  FetchAllCustomFieldsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchAllCustomFields),
      tap(() => {
        this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: true }));
      }),
      switchMap(() =>
        this.settingsService.listCustomFields({ number: 1, size: DEFAULT_PAGE_SIZE }).pipe(
          map((res: APIResponse<ICustomField>) => settingsActions.FetchAllCustomFieldsSuccess({ customFields: res })),
          catchError(() => of(settingsActions.FetchAllCustomFieldsError())),
          finalize(() => {
            this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // fetch Custom Fields page (infinite scroll)
  FetchCustomFieldsPageEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.FetchCustomFieldsPage),
      tap(() => {
        this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.listCustomFields(action.page, action.object_type).pipe(
          map((res: APIResponse<ICustomField>) =>
            settingsActions.FetchCustomFieldsPageSuccess({ customFields: res, append: action.append })
          ),
          catchError(() => of(settingsActions.FetchCustomFieldsPageError())),
          finalize(() => {
            this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // add Custom Field
  AddCustomFieldEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.AddCustomField),
      tap(() => {
        this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.addCustomField(action.customField).pipe(
          map((res: APIResponse<ICustomField>) => settingsActions.AddCustomFieldSuccess({ customField: res.object! })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.customFieldAddSuccess}`), 'SUCCESS');
          }),
          catchError(() => of(settingsActions.AddCustomFieldError())),
          finalize(() => {
            this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // update Custom Field
  UpdateCustomFieldEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateCustomField),
      tap(() => {
        this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.updateCustomField(action.id, action.customField).pipe(
          map((res: APIResponse<ICustomField>) => settingsActions.UpdateCustomFieldSuccess({ customField: res.object! })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.customFieldUpdateSuccess}`), 'SUCCESS');
          }),
          catchError(() => of(settingsActions.UpdateCustomFieldError())),
          finalize(() => {
            this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // delete Custom Field
  DeleteCustomFieldEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.DeleteCustomField),
      tap(() => {
        this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.deleteCustomField(action.id).pipe(
          map((res: APIResponse<ICustomField>) => settingsActions.DeleteCustomFieldSuccess({ id: action.id })),
          tap(() => {
            this.commonService.showToast(this.translateService.instant(`alerts.${AlertEnums.customFieldDeleteSuccess}`), 'SUCCESS');
          }),
          catchError(() => of(settingsActions.DeleteCustomFieldError())),
          finalize(() => {
            this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // Get Object Custom Fields
  GetObjectCustomFieldsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.GetObjectCustomFields),
      tap(() => {
        this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.getObjectCustomFields(action.params).pipe(
          map((res: APIResponse<IObjectCustomFields>) => settingsActions.GetObjectCustomFieldsSuccess({ objectCustomFields: res.object! })),
          catchError(() => of(settingsActions.GetObjectCustomFieldsError())),
          finalize(() => {
            this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // Update Object Custom Fields values
  UpdateObjectCustomFieldsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateObjectCustomFields),
      tap(() => {
        this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: true }));
      }),
      switchMap((action) =>
        this.settingsService.updateObjectCustomFields(action.params, action.values).pipe(
          map((res: APIResponse<IObjectCustomFields>) => {
            return settingsActions.UpdateObjectCustomFieldsSuccess({ objectCustomFields: res.object! });
          }),
          tap(() => {
            this.commonService.showToast('Custom fields saved', 'SUCCESS');
            this.store.dispatch(
              settingsActions.GetObjectCustomFields({
                params: action.params,
              })
            );
          }),
          catchError(() =>
            of(settingsActions.UpdateObjectCustomFieldsError()),
          ),
          finalize(() => {
            this.store.dispatch(settingsActions.SetCustomFieldsLoader({ isLoading: false }));
          })
        )
      )
    )
  );

  // Orders Settings
  GetOrdersSettingsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.GetOrdersSettings),
      switchMap(() =>
        this.settingsService.getOrdersSettings().pipe(
          map((res: APIResponse<IOrdersSettings>) =>
            settingsActions.GetOrdersSettingsSuccess({ settings: res.object! })
          ),
          catchError(() => of(settingsActions.GetOrdersSettingsError()))
        )
      )
    )
  );

  UpdateOrdersSettingsEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateOrdersSettings),
      switchMap((action) =>
        this.settingsService.updateOrdersSettings(action.settings).pipe(
          map((res: APIResponse<IOrdersSettings>) =>
            settingsActions.UpdateOrdersSettingsSuccess({ settings: res.object! })
          ),
          tap(() => {
            this.commonService.showToast(
              this.translateService.instant('settings.orders_settings_saved'),
              'SUCCESS'
            );
          }),
          catchError(() => of(settingsActions.UpdateOrdersSettingsError()))
        )
      )
    )
  );

  // Splitting Rules
  LoadSplittingRulesEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.LoadSplittingRules),
      switchMap(() =>
        this.settingsService.listSplittingRules().pipe(
          map((res: any) =>
            settingsActions.LoadSplittingRulesSuccess({ rules: res.data || [] })
          ),
          catchError(() => of(settingsActions.LoadSplittingRulesError()))
        )
      )
    )
  );

  CreateSplittingRuleEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.CreateSplittingRule),
      switchMap((action) =>
        this.settingsService.createSplittingRule(action.rule).pipe(
          map((res: any) =>
            settingsActions.CreateSplittingRuleSuccess({ rule: res.object })
          ),
          tap(() => {
            this.commonService.showToast('Splitting rule created', 'SUCCESS');
          }),
          catchError(() => of(settingsActions.CreateSplittingRuleError()))
        )
      )
    )
  );

  UpdateSplittingRuleEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.UpdateSplittingRule),
      switchMap((action) =>
        this.settingsService.updateSplittingRule(action.id, action.rule).pipe(
          map((res: any) =>
            settingsActions.UpdateSplittingRuleSuccess({ rule: res.object })
          ),
          tap(() => {
            this.commonService.showToast('Splitting rule updated', 'SUCCESS');
          }),
          catchError(() => of(settingsActions.UpdateSplittingRuleError()))
        )
      )
    )
  );

  DeleteSplittingRuleEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.DeleteSplittingRule),
      switchMap((action) =>
        this.settingsService.deleteSplittingRule(action.id).pipe(
          map(() =>
            settingsActions.DeleteSplittingRuleSuccess({ id: action.id })
          ),
          tap(() => {
            this.commonService.showToast('Splitting rule deleted', 'SUCCESS');
          }),
          catchError(() => of(settingsActions.DeleteSplittingRuleError()))
        )
      )
    )
  );
}
